import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Stack,
  IconButton,
  Button,
  Paper,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  InputAdornment,
  Tooltip,
  Chip,
  Avatar,
  Fab
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDraggable,
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable';
import { Job, JobStatus, JobCreate, createJob, getJobs, updateJobStatus, deleteJob } from '../services/api';
import { useSnackbar } from 'notistack';

// Constants
const COLUMNS: { id: JobStatus; title: string }[] = [
  { id: JobStatus.APPLIED, title: 'Applied' },
  { id: JobStatus.INTERVIEWING, title: 'Interviewing' },
  { id: JobStatus.OFFERED, title: 'Offered' },
  { id: JobStatus.REJECTED, title: 'Rejected' }
];

// Types
interface JobCardProps {
  job: Job;
  onDelete: (jobId: string) => void;
}

interface ColumnProps {
  id: JobStatus;
  title: string;
  jobs: Job[];
  onDelete: (jobId: string) => void;
}

interface AddJobDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (job: JobCreate) => Promise<void>;
}

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  jobTitle: string;
}

interface JobDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  job: Job | null;
}

// Components
const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({ open, onClose, onConfirm, jobTitle }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete the job application for "{jobTitle}"? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const JobDetailsDialog: React.FC<JobDetailsDialogProps> = ({ open, onClose, job }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!job) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 48,
                height: 48
              }}
            >
              <WorkIcon />
            </Avatar>
            <Stack>
              <Typography variant="h6">{job.title}</Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {job.company}
              </Typography>
            </Stack>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Chip
              label={getStatusLabel(job.status)}
              color={getStatusColor(job.status)}
              sx={{ fontWeight: 500 }}
            />
            <Chip
              icon={<CalendarIcon />}
              label={`Applied on ${formatDate(job.date)}`}
              variant="outlined"
            />
            {job.location && (
              <Chip
                icon={<LocationIcon />}
                label={job.location}
                variant="outlined"
              />
            )}
            {job.salary && (
              <Chip
                icon={<MoneyIcon />}
                label={job.salary}
                variant="outlined"
              />
            )}
          </Stack>

          {job.description && (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Job Description
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 300,
                  overflow: 'auto',
                  backgroundColor: 'background.default'
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    m: 0
                  }}
                >
                  {job.description}
                </Typography>
              </Paper>
            </Box>
          )}

          {job.url && (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Job Link
              </Typography>
              <Button
                href={job.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<LinkIcon />}
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
                View Original Job Posting
              </Button>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const getStatusColor = (status: JobStatus) => {
  switch (status) {
    case JobStatus.APPLIED:
      return 'info';
    case JobStatus.INTERVIEWING:
      return 'warning';
    case JobStatus.OFFERED:
      return 'success';
    case JobStatus.REJECTED:
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: JobStatus) => {
  switch (status) {
    case JobStatus.APPLIED:
      return 'Applied';
    case JobStatus.INTERVIEWING:
      return 'Interviewing';
    case JobStatus.OFFERED:
      return 'Offered';
    case JobStatus.REJECTED:
      return 'Rejected';
    default:
      return status;
  }
};

const JobCard: React.FC<JobCardProps> = React.memo(({ job, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: job.id,
    data: job,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setIsDeleteDialogOpen(false);
    onDelete(job.id);
  };

  const handleViewDetails = () => {
    handleMenuClose();
    setIsDetailsDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <Card
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        style={style}
        sx={{
          p: 2,
          mb: 1.5,
          cursor: 'grab',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: 3,
            borderColor: 'primary.dark',
            transform: 'translateY(-2px)'
          }
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 40,
                  height: 40,
                  flexShrink: 0
                }}
              >
                <WorkIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography 
                  variant="h6" 
                  noWrap 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                >
                  {job.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  noWrap
                >
                  {job.company}
                </Typography>
              </Box>
            </Stack>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ ml: 1, flexShrink: 0 }}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={getStatusLabel(job.status)}
              color={getStatusColor(job.status)}
              size="small"
              sx={{ fontWeight: 500 }}
            />
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                whiteSpace: 'nowrap'
              }}
            >
              <CalendarIcon sx={{ fontSize: 14, mr: 0.5 }} />
              {formatDate(job.date)}
            </Typography>
          </Stack>

          {(job.location || job.salary) && (
            <Stack 
              direction="row" 
              spacing={2} 
              flexWrap="wrap" 
              useFlexGap
              sx={{ gap: 1 }}
            >
              {job.location && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <LocationIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {job.location}
                </Typography>
              )}
              {job.salary && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <MoneyIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {job.salary}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleViewDetails}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Card>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        jobTitle={job.title}
      />

      <JobDetailsDialog
        open={isDetailsDialogOpen}
        onClose={() => setIsDetailsDialogOpen(false)}
        job={job}
      />
    </>
  );
});

const Column: React.FC<ColumnProps> = React.memo(({ id, title, jobs, onDelete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: id,
  });

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.APPLIED:
        return theme.palette.info.light;
      case JobStatus.INTERVIEWING:
        return theme.palette.warning.light;
      case JobStatus.OFFERED:
        return theme.palette.success.light;
      case JobStatus.REJECTED:
        return theme.palette.error.light;
      default:
        return theme.palette.grey[200];
    }
  };

  const getStatusIcon = (status: JobStatus) => {
    switch (status) {
      case JobStatus.APPLIED:
        return <DescriptionIcon />;
      case JobStatus.INTERVIEWING:
        return <WorkIcon />;
      case JobStatus.OFFERED:
        return <MoneyIcon />;
      case JobStatus.REJECTED:
        return <CloseIcon />;
      default:
        return <WorkIcon />;
    }
  };

  return (
    <Paper
      ref={setNodeRef}
      sx={{
        p: 2,
        minHeight: 500,
        width: isMobile ? '100%' : 300,
        backgroundColor: theme.palette.background.default,
        border: '1px solid',
        borderColor: isOver ? 'primary.main' : 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
        }
      }}
    >
      <Stack spacing={2}>
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={1}
          sx={{
            p: 1,
            borderRadius: 1,
            backgroundColor: getStatusColor(id),
            color: theme.palette.getContrastText(getStatusColor(id))
          }}
        >
          {getStatusIcon(id)}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Chip
            label={jobs.length}
            size="small"
            sx={{
              ml: 'auto',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'inherit',
              fontWeight: 500
            }}
          />
        </Stack>

        <Stack 
          spacing={1.5}
          sx={{
            minHeight: 100,
            p: isOver ? 1 : 0,
            borderRadius: 1,
            backgroundColor: isOver ? 'action.hover' : 'transparent',
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {jobs.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                color: 'text.secondary',
                border: '2px dashed',
                borderColor: isOver ? 'primary.main' : 'divider',
                borderRadius: 1,
                minHeight: 100,
                transition: 'all 0.2s ease-in-out',
                backgroundColor: isOver ? 'action.hover' : 'transparent',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {isOver ? (
                <Stack spacing={1} alignItems="center">
                  <WorkIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="body2" align="center" color="primary.main" fontWeight={500}>
                    Drop here to move
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={1.5} alignItems="center">
                  <WorkIcon sx={{ fontSize: 32, opacity: 0.5 }} />
                  <Typography variant="body2" align="center" fontWeight={500}>
                    No jobs in {title.toLowerCase()}
                  </Typography>
                  <Typography variant="caption" align="center" color="text.secondary">
                    {id === JobStatus.APPLIED ? 'Start by adding your first application' :
                     id === JobStatus.INTERVIEWING ? 'Move applications here when you get interviews' :
                     id === JobStatus.OFFERED ? 'Track your job offers here' :
                     'Mark applications as rejected here'}
                  </Typography>
                </Stack>
              )}
            </Box>
          ) : (
            jobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                onDelete={onDelete}
              />
            ))
          )}
        </Stack>
      </Stack>
    </Paper>
  );
});

const AddJobDialog: React.FC<AddJobDialogProps> = React.memo(({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<JobCreate>({
    title: '',
    company: '',
    status: JobStatus.APPLIED,
    date: new Date().toISOString().split('T')[0],
    location: '',
    salary: '',
    description: '',
    url: ''
  });

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<JobStatus>) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.value as JobStatus
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSubmit(formData);
      onClose();
      // Reset form
      setFormData({
        title: '',
        company: '',
        status: JobStatus.APPLIED,
        date: new Date().toISOString().split('T')[0],
        location: '',
        salary: '',
        description: '',
        url: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Add New Job</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
              <TextField
                required
                fullWidth
                label="Job Title"
                name="title"
                value={formData.title}
                onChange={handleTextChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                fullWidth
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleTextChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleSelectChange}
                >
                  {COLUMNS.map(({ id, title }) => (
                    <MenuItem key={id} value={id}>
                      {title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                required
                fullWidth
                label="Application Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleTextChange}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Stack>

            <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleTextChange}
                placeholder="e.g., San Francisco, CA or Remote"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Salary Range"
                name="salary"
                value={formData.salary}
                onChange={handleTextChange}
                placeholder="e.g., $120k - $150k"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <TextField
              fullWidth
              label="Job Description"
              name="description"
              value={formData.description}
              onChange={handleTextChange}
              multiline
              rows={3}
              size="small"
              placeholder="Paste the job description here..."
            />

            <TextField
              fullWidth
              label="Job URL"
              name="url"
              value={formData.url}
              onChange={handleTextChange}
              placeholder="https://..."
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Add Job
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
});

// Main Component
const JobManagement: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedJobs = await getJobs();
      setJobs(fetchedJobs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
      enqueueSnackbar('Failed to fetch jobs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeJob = jobs.find(job => job.id === active.id);
    if (!activeJob) return;

    const newStatus = over.id as JobStatus;
    if (activeJob.status === newStatus) return;

    // Optimistic update
    setJobs(prevJobs => 
      prevJobs.map(job => 
        job.id === active.id ? { ...job, status: newStatus } : job
      )
    );

    try {
      await updateJobStatus(active.id as string, newStatus);
      enqueueSnackbar('Job status updated', { variant: 'success' });
    } catch (err) {
      // Revert on error
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === active.id ? { ...job, status: activeJob.status } : job
        )
      );
      enqueueSnackbar('Failed to update job status', { variant: 'error' });
    }
  };

  // Handle job deletion
  const handleDeleteJob = async (jobId: string) => {
    // Optimistic update
    setJobs(prevJobs => prevJobs.filter(job => job.id !== jobId));

    try {
      await deleteJob(jobId);
      enqueueSnackbar('Job deleted successfully', { variant: 'success' });
    } catch (err) {
      // Revert on error
      fetchJobs();
      enqueueSnackbar('Failed to delete job', { variant: 'error' });
    }
  };

  // Handle add job
  const handleAddJob = async (jobData: JobCreate) => {
    try {
      const newJob = await createJob(jobData);
      setJobs(prevJobs => [...prevJobs, newJob]);
      enqueueSnackbar('Job added successfully', { variant: 'success' });
    } catch (err) {
      throw err; // Let the dialog handle the error
    }
  };

  // Group jobs by status
  const jobsByStatus = jobs.reduce((acc, job) => {
    if (!acc[job.status]) {
      acc[job.status] = [];
    }
    acc[job.status].push(job);
    return acc;
  }, {} as Record<JobStatus, Job[]>);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Stack spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Job Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage your job applications
          </Typography>
        </Stack>
        {isMobile ? (
          <Tooltip title="Add New Job">
            <Fab
              color="primary"
              onClick={() => setIsAddDialogOpen(true)}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                zIndex: 1000,
                boxShadow: 4,
                '&:hover': {
                  transform: 'scale(1.05)',
                },
                transition: 'transform 0.2s ease-in-out'
              }}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddDialogOpen(true)}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Add New Job
          </Button>
        )}
      </Stack>

      <AddJobDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSubmit={handleAddJob}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          sx={{ 
            width: '100%',
            overflowX: 'auto',
            pb: 2,
            '& > *': {
              flex: 1,
              minWidth: 300
            }
          }}
        >
          {COLUMNS.map(({ id, title }) => (
            <Column
              key={id}
              id={id}
              title={title}
              jobs={jobs.filter(job => job.status === id)}
              onDelete={handleDeleteJob}
            />
          ))}
        </Stack>

        <DragOverlay>
          {activeId ? (
            <JobCard
              job={jobs.find(job => job.id === activeId)!}
              onDelete={handleDeleteJob}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </Box>
  );
};

export default JobManagement; 